import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Loader2, CalendarCheck, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TopicAnalyticsOverview } from "@/components/analytics/TopicAnalyticsOverview";
import { WeakAreasPanel } from "@/components/analytics/WeakAreasPanel";
import { AITopicSuggestions } from "@/components/analytics/AITopicSuggestions";
import { FocusedPlanWizard } from "@/components/study-plan/FocusedPlanWizard";
import { useState } from "react";

export default function TopicAnalytics() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const [showPlanWizard, setShowPlanWizard] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["topic-analytics", topicId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-topic-analytics', {
        body: { topicId },
      });

      if (error) throw error;
      return data;
    },
    enabled: !!topicId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Analytics</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : "Failed to load topic analytics"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data?.insufficientData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>{data.topicName}</CardTitle>
            <CardDescription>Insufficient Data for Analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              You need at least {data.minimumRequired} attempts to view analytics for this topic.
            </p>
            <p className="text-sm text-muted-foreground">
              Current attempts: {data.currentAttempts}
            </p>
            <Button 
              onClick={() => navigate(`/practice?topic=${topicId}`)} 
              className="mt-4"
            >
              Start Practicing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{data?.topicName}</h1>
            <p className="text-muted-foreground">Performance Analytics</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/practice?topic=${topicId}`)}
            >
              <Target className="mr-2 h-4 w-4" />
              Practice Now
            </Button>
            
            <Button
              onClick={() => setShowPlanWizard(true)}
              className="bg-gradient-to-r from-primary to-primary/80"
            >
              <CalendarCheck className="mr-2 h-4 w-4" />
              Create Study Plan
            </Button>
          </div>
        </div>
      </div>
      
      {data && (
        <FocusedPlanWizard
          isOpen={showPlanWizard}
          onClose={() => setShowPlanWizard(false)}
          topicId={topicId!}
          topicName={data.topicName}
          currentAccuracy={data.overallAccuracy}
          weakAreas={data.tagPerformance?.filter((t: any) => t.weakArea) || []}
          questionsAttempted={data.totalAttempts}
        />
      )}

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="weak-areas">Weak Areas</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <TopicAnalyticsOverview data={data} />
        </TabsContent>

        <TabsContent value="weak-areas" className="space-y-6">
          <WeakAreasPanel 
            weakAreas={data?.tagPerformance?.filter((t: any) => t.weakArea) || []}
            difficultyIssues={data?.difficultyBreakdown?.filter((d: any) => d.weakArea) || []}
            commonMistakes={data?.commonMistakes || []}
            topicId={topicId!}
          />
          <AITopicSuggestions suggestions={data?.aiSuggestions || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
